import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useState,
} from "react";
import { siteTexts } from "../../../../shared/i18n/siteTexts";
import { AuthUser } from "../types/auth";
import * as authService from "../services/authService";

type AuthContextValue = {
  user: AuthUser | null;
  token: string;
  loading: boolean;
  isAdmin: boolean;
  isOrganizer: boolean;
  isStaff: boolean;
  signIn: (identifier: string, password: string) => Promise<void>;
  signUp: (
    name: string,
    email: string,
    password: string,
    cpf: string,
    phone?: string,
    sex?: AuthUser["sex"],
    category?: AuthUser["category"],
  ) => Promise<void>;
  signOut: () => Promise<void>;
  completeCpf: (cpf: string, phone?: string) => Promise<void>;
  updateProfile: (
    payload: Partial<
      Pick<
        AuthUser,
        "name" | "email" | "cpf" | "phone" | "sex" | "category" | "avatarUrl"
      >
    >,
  ) => Promise<AuthUser>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  requestEmailConfirmationLink: () => Promise<{
    ok: boolean;
    pendingEmail?: string;
    emailChangeConfirmationLink?: string;
    emailChangeExpiresAt?: string;
  }>;
  refreshUser: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);
const TOKEN_KEY = "tnp-token";

export function AuthProvider({ children }: PropsWithChildren) {
  const [token, setToken] = useState<string>(() => {
    return localStorage.getItem(TOKEN_KEY) || "";
  });
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState<boolean>(Boolean(token));

  const refreshUser = async () => {
    if (!token) {
      setUser(null);
      return;
    }
    const current = await authService.me(token);
    setUser(current);
  };

  useEffect(() => {
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    authService
      .me(token)
      .then((data) => {
        if (!active) return;
        setUser(data);
      })
      .catch(() => {
        if (!active) return;
        localStorage.removeItem(TOKEN_KEY);
        setToken("");
        setUser(null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
    };
  }, [token]);

  const signIn = async (identifier: string, password: string) => {
    const payload = await authService.login(identifier, password);
    localStorage.setItem(TOKEN_KEY, payload.token);
    setToken(payload.token);
    setUser(payload.user);
  };

  const signUp = async (
    name: string,
    email: string,
    password: string,
    cpf: string,
    phone = "",
    sex: AuthUser["sex"] = "",
    category: AuthUser["category"] = "",
  ) => {
    const payload = await authService.register(
      name,
      email,
      password,
      cpf,
      phone,
      sex,
      category,
    );
    localStorage.setItem(TOKEN_KEY, payload.token);
    setToken(payload.token);
    setUser(payload.user);
  };

  const signOut = async () => {
    if (token) {
      try {
        await authService.logout(token);
      } catch (_error) {
        // Ignorar erro de logout no backend.
      }
    }
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setUser(null);
  };

  const completeCpf = async (cpf: string, phone = "") => {
    if (!token) throw new Error(siteTexts.auth.context.invalidSession);
    const updated = await authService.completeProfile(token, cpf, phone);
    setUser(updated);
  };

  const updateProfile = async (
    payload: Partial<
      Pick<
        AuthUser,
        "name" | "email" | "cpf" | "phone" | "sex" | "category" | "avatarUrl"
      >
    >
  ) => {
    if (!token) throw new Error(siteTexts.auth.context.invalidSession);
    const updated = await authService.updateMyProfile(token, payload);
    setUser(updated);
    return updated;
  };

  const changePassword = async (currentPassword: string, newPassword: string) => {
    if (!token) throw new Error(siteTexts.auth.context.invalidSession);
    await authService.changeMyPassword(token, currentPassword, newPassword);
  };

  const requestEmailConfirmationLink = async () => {
    if (!token) throw new Error(siteTexts.auth.context.invalidSession);
    return authService.requestEmailConfirmationLink(token);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        token,
        loading,
        isAdmin: user?.role === "admin",
        isOrganizer: user?.role === "organizer",
        isStaff: user?.role === "admin" || user?.role === "organizer",
        signIn,
        signUp,
        signOut,
        completeCpf,
        updateProfile,
        changePassword,
        requestEmailConfirmationLink,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error(siteTexts.auth.context.hookOutOfScope);
  }
  return context;
}
